import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
  HStack,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import {
  Square,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { ViewStyle } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
  useCloseOralExamCohort,
} from '@/common/api/endpoints/oral-exams/oral-exams';
import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';
import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';
import { useMeetingConfirm } from '@/common/components/meeting/MeetingConfirm';
import { useDialog } from '@/common/context/DialogContext';
import { getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';
import { resolveLecturerCohortTileGate } from '@/common/hooks/oralExam/oralExamCtaGateMatrix';
import { useAuthStore } from '@/common/auth/store';
import { useOralExamCohortState } from '@/common/hooks/oralExam/useOralExamCohortState';
import {
  colleagueVisitsOnBooking,
  otherConnectedSupervisorNames,
  viewerQuietObserveBookingId,
  visitHolderAboveObserving,
} from '@/common/oralExam/supervisorVisits';
import { RECORDING_NOT_REQUIRED_INFO } from '@/common/oralExam/recordingGateCopy';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import { hasInProgressOralExamRuns } from '@/common/utils/oralExam/runConduct';
import { OperationalFaultStudentBanner } from '@/student/module/components/OperationalFaultStudentBanner';

import {
  StudentLiveTile,
  cohortConsoleBand,
  isLiveRosterBooking,
  needsYou,
  ReadinessJourneyLabel,
} from './StudentLiveTile';
import { voidReasonLine } from './voidReasonLine';

export type CohortConsoleProps = {
  slotId: string;
  onDropIn: (bookingId: string) => void;
  /** Observe on the wall — a private zoom. The student still reads Watching. */
  onObserve?: (bookingId: string) => Promise<void>;
  /**
   * Return to overview. Closes the zoom when one is open, and takes the
   * supervisor off the student's screen.
   */
  onHide?: () => Promise<void>;
  /** Leave Engage — clears speaking rung and pause while staying on the wall. */
  onLeave?: () => Promise<void>;
  onNext?: (bookingId: string) => void;
  /** Abort one student's run (removes a stranded/expired student from the cohort). */
  onRemove?: (summary: OralExamBookingRunSummary) => Promise<unknown>;
  /** Soft-reset one student's run (same mutation as drop-in Run again). */
  onReset?: (summary: OralExamBookingRunSummary) => Promise<unknown>;
  /** Cancel booking and allow re-book (left-behind uncommitted attempt). */
  onCancel?: (summary: OralExamBookingRunSummary) => Promise<unknown>;
  /** True while a supervision rung write for this slot is in flight. */
  isRungMutationPending?: boolean;
};

function ifMatchHeaders(etag: string | null | undefined): { 'If-Match': string } | undefined {
  if (!etag) return undefined;
  return { 'If-Match': etag };
}

function waitKeyMs(summary: OralExamBookingRunSummary): number | null {
  const ts = summary.runState.studentDoneAt ?? summary.runState.questionMarkerStartedAt;
  if (!ts) return null;
  return new Date(ts).getTime();
}

const WALL_GRID_COLUMNS = 3;
/**
 * The wall and the submitted list share one width: 80% of the pane, capped so a
 * large monitor does not blow one card up to half the screen. Column count stays
 * fixed so one student is the same size as six.
 */
const WALL_WIDTH = '80%';
const WALL_MAX_WIDTH_PX = 1440;

function wallOnTrackGridStyle(): ViewStyle {
  return {
    display: 'grid' as const,
    gridTemplateColumns: `repeat(${WALL_GRID_COLUMNS}, minmax(0, 1fr))`,
    // Cards keep their natural 16:9 height; the console body scrolls instead of
    // squeezing rows (squeezed cells clipped the cards).
    gridAutoRows: 'max-content',
    gap: 16,
    width: WALL_WIDTH,
    maxWidth: WALL_MAX_WIDTH_PX,
    flexShrink: 0,
  } as unknown as ViewStyle;
}

function wallWidthStyle(): ViewStyle {
  return { width: WALL_WIDTH, maxWidth: WALL_MAX_WIDTH_PX } as unknown as ViewStyle;
}

/** Wall and bands scroll together; the action bar below stays pinned. */
function wallScrollStyle(): ViewStyle {
  return { overflowY: 'auto' as const } as unknown as ViewStyle;
}

/** Observe stage: hero column above a fixed peer strip (sibling, not stacked grid rows). */
function observeStageColumnStyle(): ViewStyle {
  return {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    alignItems: 'stretch' as const,
    gap: 12,
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden' as const,
  } as unknown as ViewStyle;
}

function observeHeroCellStyle(): ViewStyle {
  return {
    width: '100%',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden' as const,
  } as unknown as ViewStyle;
}

/**
 * One row of at most five faces, always five columns.
 * Column count stays fixed so one peer is the same size as five. A single
 * full-width 16:9 card is taller than a wide window and collapses the hero.
 */
const OBSERVE_STRIP_COLUMNS = 5;

function observeStripRowStyle(): ViewStyle {
  return {
    display: 'grid' as const,
    gridTemplateColumns: `repeat(${OBSERVE_STRIP_COLUMNS}, minmax(0, 1fr))`,
    alignItems: 'start' as const,
    gap: 12,
    flexShrink: 0,
    width: '100%',
  } as unknown as ViewStyle;
}

function observeStripCellStyle(): ViewStyle {
  return {
    minWidth: 0,
    minHeight: 0,
  } as unknown as ViewStyle;
}

function wallTileCellStyle(): ViewStyle {
  return {
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden' as const,
  };
}

function compareNeedsYou(a: OralExamBookingRunSummary, b: OralExamBookingRunSummary): number {
  const wa = waitKeyMs(a);
  const wb = waitKeyMs(b);
  if (wa === null && wb === null) return 0;
  if (wa === null) return 1;
  if (wb === null) return -1;
  // TODO(S3e-followup): use a real pause-started-at once exposed
  return wa - wb;
}

function formatSupervisionCountdown(deadlineAt: string, nowMs: number): string {
  const remainingSec = Math.max(0, Math.floor((Date.parse(deadlineAt) - nowMs) / 1000));
  const minutes = Math.floor(remainingSec / 60);
  const seconds = remainingSec % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function SupervisionAbsenceCountdown({ deadlineAt }: { deadlineAt: string }) {
  const { t } = useTranslation();
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    setNowMs(Date.now());
    const timerId = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(timerId);
  }, [deadlineAt]);

  const time = formatSupervisionCountdown(deadlineAt, nowMs);
  const message = t('oralExam.lecturer.cohort.supervisionAbsenceCountdown', {
    defaultValue:
      "Not enough supervisors present — students' sessions end in {{time}} unless a supervisor joins.",
    time,
  });
  const timeIndex = message.indexOf(time);

  return (
    <Box
      width="100%"
      bg="$statusWarningSubtle"
      borderLeftWidth={4}
      borderLeftColor="$statusWarning"
      p="$3"
      testID="cohort-supervision-countdown"
    >
      {timeIndex >= 0 ? (
        <Text size="sm" color="$textOnWarningSubtle">
          {message.slice(0, timeIndex)}
          <Text size="sm" color="$textOnWarningSubtle" fontFamily="$heading">
            {time}
          </Text>
          {message.slice(timeIndex + time.length)}
        </Text>
      ) : (
        <Text size="sm" color="$textOnWarningSubtle">
          {message}
        </Text>
      )}
    </Box>
  );
}

/** Void-family abort — student must rebook; cause comes from server voidCause / endReason. */
function VoidStudentTile({ summary }: { summary: OralExamBookingRunSummary }) {
  const { t } = useTranslation();
  const { bookingId, studentName } = summary;
  const line = voidReasonLine(summary.runState);

  return (
    <Box
      bg="$bgCard"
      borderRadius="$md"
      borderWidth={1}
      borderColor="$borderLight"
      overflow="hidden"
      testID={`student-live-tile-${bookingId}`}
    >
      <VStack p="$3" space="sm">
        <Text size="md" color="$textHeading" fontWeight="bold" numberOfLines={1}>
          {studentName}
        </Text>
        <Text size="sm" color="$textBody">
          {t(line.key, { defaultValue: line.defaultValue })}
        </Text>
      </VStack>
    </Box>
  );
}

/** Aborted / not-completed tile — no Drop in (exam has ended). */
function NotCompletedStudentTile({ summary }: { summary: OralExamBookingRunSummary }) {
  const { t } = useTranslation();
  const { bookingId, studentName } = summary;

  return (
    <Box
      bg="$bgCard"
      borderRadius="$md"
      borderWidth={1}
      borderColor="$borderLight"
      overflow="hidden"
      testID={`student-live-tile-${bookingId}`}
    >
      <VStack p="$3" space="sm">
        <Text size="md" color="$textHeading" fontWeight="bold" numberOfLines={1}>
          {studentName}
        </Text>
        <Text size="sm" color="$textBody">
          {t('oralExam.lecturer.cohort.notCompletedHint', {
            defaultValue: 'Exam ended before submission',
          })}
        </Text>
      </VStack>
    </Box>
  );
}

/** Registered / disconnected tile — grayed, no live actions. */
function RegisteredStudentTile({ summary }: { summary: OralExamBookingRunSummary }) {
  const { bookingId, studentName } = summary;

  return (
    <Box
      bg="$bgCard"
      borderRadius="$md"
      borderWidth={1}
      borderColor="$borderLight"
      overflow="hidden"
      opacity={0.55}
      testID={`student-registered-tile-${bookingId}`}
    >
      <VStack p="$3" space="xs">
        <Text size="md" color="$textHeading" fontWeight="bold" numberOfLines={1}>
          {studentName ?? bookingId}
        </Text>
        <ReadinessJourneyLabel summary={summary} />
      </VStack>
    </Box>
  );
}

/** Pre-flight / ID-review journey tile during cohort supervision. */
function PreflightJourneyTile({ summary }: { summary: OralExamBookingRunSummary }) {
  const { bookingId, studentName } = summary;

  return (
    <Box
      bg="$bgCard"
      borderRadius="$md"
      borderWidth={1}
      borderColor="$borderLight"
      overflow="hidden"
      testID={`student-preflight-tile-${bookingId}`}
    >
      <VStack p="$3" space="xs">
        <Text size="md" color="$textHeading" fontWeight="bold" numberOfLines={1}>
          {studentName ?? bookingId}
        </Text>
        <ReadinessJourneyLabel summary={summary} />
      </VStack>
    </Box>
  );
}

/**
 * Cohort supervision console — triage bands and per-student cards (oral-exam-1n-lecturer-ux §3.4, §4).
 */
export function CohortConsole({
  slotId,
  onDropIn,
  onObserve,
  onHide,
  onLeave,
  onNext,
  onRemove,
  onReset,
  onCancel,
  isRungMutationPending = false,
}: CohortConsoleProps) {
  const { t } = useTranslation();
  const { confirm } = useMeetingConfirm();
  const { showDialog, hideDialog, showToastMessage } = useDialog();
  const queryClient = useQueryClient();
  const viewerLecturerId = useAuthStore((s) => s.user?.id ?? null);
  const {
    bookingRuns, etag, queryKey, cohort,
  } = useOralExamCohortState(slotId, { polling: true });
  const quietObserveBookingId = viewerQuietObserveBookingId(
    cohort?.supervisors,
    viewerLecturerId,
  );
  const [closeError, setCloseError] = useState<string | null>(null);
  const [rungWriteInFlight, setRungWriteInFlight] = useState(false);
  const [observeErrorByBooking, setObserveErrorByBooking] = useState<Record<string, string>>({});
  const [returnToOverviewError, setReturnToOverviewError] = useState<string | null>(null);
  const canCloseCohort = cohort?.sessionPhase === OralExamSessionPhase.in_exam
    && Boolean(cohort?.inExamEnteredAt);
  const etagRef = useRef(etag);
  etagRef.current = etag;
  const bookingRunsRef = useRef(bookingRuns);
  bookingRunsRef.current = bookingRuns;
  const endInFlightRef = useRef(false);
  const removeInFlightRef = useRef(false);
  const resetInFlightRef = useRef(false);
  const cancelInFlightRef = useRef(false);
  const rungControlsDisabled = isRungMutationPending || rungWriteInFlight;

  const closeCohortMutation = useCloseOralExamCohort({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  const askInProgressCloseOutcome = useCallback((
    count: number,
  ): Promise<'grade' | 'void' | null> => new Promise((resolve) => {
    let settled = false;
    const finish = (value: 'grade' | 'void' | null) => {
      if (settled) return;
      settled = true;
      hideDialog();
      resolve(value);
    };
    showDialog({
      title: t('oralExam.lecturer.cohort.endDialogTitle', { defaultValue: 'End exam' }),
      message: t('oralExam.lecturer.cohort.endDialogBodyInProgress', {
        defaultValue:
          '{{count}} student(s) are still mid-exam. What should happen to their attempt?',
        count,
      }),
      buttons: [
        {
          text: t('common.buttons.cancel', { defaultValue: 'Cancel' }),
          variant: 'outline',
          action: 'secondary',
          testID: 'cohort-end-dialog-cancel',
          onPress: () => finish(null),
        },
        {
          text: t('oralExam.lecturer.cohort.endDialogVoidAttempt', { defaultValue: 'Void attempt' }),
          action: 'negative',
          testID: 'cohort-end-dialog-void',
          onPress: () => finish('void'),
        },
        {
          text: t('oralExam.lecturer.cohort.endDialogGradeAnswers', {
            defaultValue: 'Grade answers so far',
          }),
          action: 'primary',
          testID: 'cohort-end-dialog-grade',
          onPress: () => finish('grade'),
        },
      ],
    });
  }), [hideDialog, showDialog, t]);

  const handleConfirmEnd = useCallback(async (inProgressOutcome?: 'grade' | 'void') => {
    if (endInFlightRef.current) return;
    if (!canCloseCohort) {
      setCloseError(t('oralExam.lecturer.cohort.closeNotReady', {
        defaultValue: 'The cohort is not ready to end yet.',
      }));
      return;
    }
    endInFlightRef.current = true;
    setCloseError(null);
    try {
      await closeCohortMutation.mutateAsync({
        slotId,
        data: inProgressOutcome ? { inProgressOutcome } : null,
        headers: ifMatchHeaders(etagRef.current),
      });
      showToastMessage('success', t('oralExam.lecturer.cohort.endSuccess', {
        defaultValue: 'Exam ended for the cohort.',
      }));
    } catch (error) {
      const status = error instanceof AxiosError ? error.response?.status : undefined;
      if (status === 412) {
        queryClient.invalidateQueries({ queryKey });
        setCloseError(t('oralExam.staleRetry', {
          defaultValue: 'The session changed — please try ending the exam again.',
        }));
        showToastMessage('info', t('oralExam.staleRetry', {
          defaultValue: 'The session changed — please try ending the exam again.',
        }));
        return;
      }
      const message = getOralExamApiErrorMessage(error, t('oralExam.actionFailed'));
      setCloseError(message);
      showToastMessage('error', message);
    } finally {
      endInFlightRef.current = false;
    }
  }, [
    canCloseCohort,
    closeCohortMutation,
    queryClient,
    queryKey,
    showToastMessage,
    slotId,
    t,
  ]);

  const handleRequestEnd = useCallback(async () => {
    if (closeCohortMutation.isPending || endInFlightRef.current) return;
    if (hasInProgressOralExamRuns(bookingRunsRef.current)) {
      const inProgressCount = bookingRunsRef.current.filter(
        (run) => run.runState.state === 'in_progress',
      ).length;
      const outcome = await askInProgressCloseOutcome(inProgressCount);
      if (!outcome) return;
      await handleConfirmEnd(outcome);
      return;
    }
    await handleConfirmEnd();
  }, [askInProgressCloseOutcome, closeCohortMutation.isPending, handleConfirmEnd]);

  const handleConfirmRemove = useCallback(async (bookingId: string) => {
    if (removeInFlightRef.current) return;
    removeInFlightRef.current = true;
    try {
      const latest = bookingRunsRef.current.find((run) => run.bookingId === bookingId);
      if (!latest) {
        queryClient.invalidateQueries({ queryKey });
        showToastMessage('info', t('oralExam.lecturer.cohort.rosterChanged', {
          defaultValue: 'The roster changed — refreshed. Try again if needed.',
        }));
        return;
      }
      if (!onRemove) return;
      await onRemove(latest);
      queryClient.invalidateQueries({ queryKey });
      showToastMessage('success', t('oralExam.lecturer.cohort.removeSuccess', {
        defaultValue: 'Student removed from the exam.',
      }));
    } catch {
      // useOralExamMutations already surfaced exactly one error toast.
    } finally {
      removeInFlightRef.current = false;
    }
  }, [onRemove, queryClient, queryKey, showToastMessage, t]);

  const handleRequestRemove = useCallback(async (bookingId: string) => {
    const summary = bookingRunsRef.current.find((run) => run.bookingId === bookingId);
    const displayName = summary?.studentName ?? summary?.studentId ?? bookingId;
    const confirmed = await confirm({
      title: t('oralExam.lecturer.cohort.removeDialogTitle', { defaultValue: 'Remove from exam' }),
      message: t('oralExam.lecturer.cohort.removeDialogBody', {
        defaultValue:
          'Remove {{name}} from this exam? Their run is ended and they leave the room. This cannot be undone.',
        name: displayName,
      }),
      confirmLabel: t('oralExam.lecturer.cohort.removeDialogConfirm', { defaultValue: 'Remove' }),
      destructive: true,
    });
    if (confirmed) {
      await handleConfirmRemove(bookingId);
    }
  }, [confirm, handleConfirmRemove, t]);

  const handleConfirmReset = useCallback(async (bookingId: string) => {
    if (resetInFlightRef.current) return;
    resetInFlightRef.current = true;
    try {
      const latest = bookingRunsRef.current.find((run) => run.bookingId === bookingId);
      if (!latest) {
        queryClient.invalidateQueries({ queryKey });
        showToastMessage('info', t('oralExam.lecturer.cohort.rosterChanged', {
          defaultValue: 'The roster changed — refreshed. Try again if needed.',
        }));
        return;
      }
      if (!onReset) return;
      await onReset(latest);
      queryClient.invalidateQueries({ queryKey });
      showToastMessage('success', t('oralExam.lecturer.cohort.resetSuccess', {
        defaultValue: 'Exam reset for the student.',
      }));
    } catch {
      // useOralExamMutations already surfaced exactly one error toast.
    } finally {
      resetInFlightRef.current = false;
    }
  }, [onReset, queryClient, queryKey, showToastMessage, t]);

  const handleRequestReset = useCallback(async (bookingId: string) => {
    const summary = bookingRunsRef.current.find((run) => run.bookingId === bookingId);
    const displayName = summary?.studentName ?? summary?.studentId ?? bookingId;
    const confirmed = await confirm({
      title: t('oralExam.lecturer.cohort.resetDialogTitle', { defaultValue: 'Reset exam?' }),
      message: t('oralExam.lecturer.cohort.resetDialogBody', {
        defaultValue:
          "Reset {{name}}'s exam? This clears their current progress and draws fresh questions. They stay in the room.",
        name: displayName,
      }),
      confirmLabel: t('oralExam.lecturer.cohort.resetDialogConfirm', { defaultValue: 'Reset exam' }),
      destructive: true,
    });
    if (confirmed) {
      await handleConfirmReset(bookingId);
    }
  }, [confirm, handleConfirmReset, t]);

  const handleConfirmCancel = useCallback(async (bookingId: string) => {
    if (cancelInFlightRef.current) return;
    cancelInFlightRef.current = true;
    try {
      const latest = bookingRunsRef.current.find((run) => run.bookingId === bookingId);
      if (!latest) {
        queryClient.invalidateQueries({ queryKey });
        showToastMessage('info', t('oralExam.lecturer.cohort.rosterChanged', {
          defaultValue: 'The roster changed — refreshed. Try again if needed.',
        }));
        return;
      }
      if (!onCancel) return;
      await onCancel(latest);
      queryClient.invalidateQueries({ queryKey });
      showToastMessage('success', t('oralExam.lecturer.cohort.cancelSuccess', {
        defaultValue: 'Booking cancelled — student can rebook.',
      }));
    } catch {
      // useOralExamMutations / mutation hook already surfaced exactly one error toast.
    } finally {
      cancelInFlightRef.current = false;
    }
  }, [onCancel, queryClient, queryKey, showToastMessage, t]);

  const handleRequestCancel = useCallback(async (bookingId: string) => {
    const summary = bookingRunsRef.current.find((run) => run.bookingId === bookingId);
    const displayName = summary?.studentName ?? summary?.studentId ?? bookingId;
    const confirmed = await confirm({
      title: t('oralExam.lecturer.cohort.cancelDialogTitle', {
        defaultValue: 'Cancel booking?',
      }),
      message: t('oralExam.lecturer.cohort.cancelDialogBody', {
        defaultValue:
          'Cancel {{name}}\'s booking? They leave this exam and can book again. Other students are not affected.',
        name: displayName,
      }),
      confirmLabel: t('oralExam.lecturer.cohort.cancelDialogConfirm', {
        defaultValue: 'Cancel & allow re-booking',
      }),
      destructive: true,
    });
    if (confirmed) {
      await handleConfirmCancel(bookingId);
    }
  }, [confirm, handleConfirmCancel, t]);

  const handleObserveToggle = useCallback(async (bookingId: string) => {
    if (rungControlsDisabled) return;

    const current = bookingRunsRef.current.find((run) => run.bookingId === bookingId);
    const isZoomedHere = current?.supervisorStatus === OralExamSupervisorStatus.here_with_you
      || quietObserveBookingId === bookingId;

    if (isZoomedHere) {
      if (!onHide) {
        setReturnToOverviewError(t('oralExam.actionFailed', { defaultValue: 'Action failed' }));
        return;
      }
      setRungWriteInFlight(true);
      setReturnToOverviewError(null);
      try {
        await onHide();
      } catch (error) {
        setReturnToOverviewError(
          getOralExamApiErrorMessage(error, t('oralExam.actionFailed', { defaultValue: 'Action failed' })),
        );
      } finally {
        setRungWriteInFlight(false);
      }
      return;
    }

    if (!onObserve) {
      setObserveErrorByBooking((prev) => ({
        ...prev,
        [bookingId]: t('oralExam.actionFailed', { defaultValue: 'Action failed' }),
      }));
      return;
    }

    setRungWriteInFlight(true);
    setObserveErrorByBooking((prev) => {
      const next = { ...prev };
      delete next[bookingId];
      return next;
    });
    try {
      await onObserve(bookingId);
    } catch (error) {
      setObserveErrorByBooking((prev) => ({
        ...prev,
        [bookingId]: getOralExamApiErrorMessage(
          error,
          t('oralExam.actionFailed', { defaultValue: 'Action failed' }),
        ),
      }));
    } finally {
      setRungWriteInFlight(false);
    }
  }, [onHide, onObserve, quietObserveBookingId, rungControlsDisabled, t]);

  const handleEngageDropIn = useCallback((bookingId: string) => onDropIn(bookingId), [onDropIn]);

  const resolveTileRecoveryHandlers = useCallback((summary: OralExamBookingRunSummary) => {
    const gate = resolveLecturerCohortTileGate({
      bookingId: summary.bookingId,
      runState: summary.runState.state,
      conductPhase: summary.runState.conductPhase,
      committed: summary.runState.committed,
      stageGateOk: summary.runState.stageGate?.ok,
      sessionPhase: cohort?.sessionPhase,
      inExamEnteredAt: cohort?.inExamEnteredAt ?? null,
      isAdmitted: summary.isAdmitted,
      isMutationPending: resetInFlightRef.current || cancelInFlightRef.current || removeInFlightRef.current,
    });
    return {
      onReset: gate.resetExam && onReset ? () => handleRequestReset(summary.bookingId) : undefined,
      onCancel: gate.cancelBooking && onCancel ? () => handleRequestCancel(summary.bookingId) : undefined,
      onRemove: onRemove ? () => handleRequestRemove(summary.bookingId) : undefined,
    };
  }, [
    cohort?.inExamEnteredAt,
    cohort?.sessionPhase,
    handleRequestCancel,
    handleRequestRemove,
    handleRequestReset,
    onCancel,
    onRemove,
    onReset,
  ]);

  const examDoorClosed = cohort?.sessionPhase === OralExamSessionPhase.in_exam
    || Boolean(cohort?.inExamEnteredAt);
  const rosterRuns = useMemo(
    () => bookingRuns.filter((summary) => isLiveRosterBooking(summary, examDoorClosed)),
    [bookingRuns, examDoorClosed],
  );

  const {
    needsYouRuns,
    inExamRuns,
    waitingExaminerRuns,
    movingRoomRuns,
    preflightActiveRuns,
    registeredRuns,
    submittedRuns,
    voidRuns,
    notCompletedRuns,
  } = useMemo(() => {
    const needs: OralExamBookingRunSummary[] = [];
    const inExam: OralExamBookingRunSummary[] = [];
    const waiting: OralExamBookingRunSummary[] = [];
    const moving: OralExamBookingRunSummary[] = [];
    const preflight: OralExamBookingRunSummary[] = [];
    const registered: OralExamBookingRunSummary[] = [];
    const submitted: OralExamBookingRunSummary[] = [];
    const voided: OralExamBookingRunSummary[] = [];
    const notCompleted: OralExamBookingRunSummary[] = [];
    const nowMs = Date.now();

    rosterRuns.forEach((summary) => {
      const band = cohortConsoleBand(summary, needsYou(summary, nowMs));
      switch (band) {
        case 'submitted':
          submitted.push(summary);
          break;
        case 'void':
          voided.push(summary);
          break;
        case 'notCompleted':
          notCompleted.push(summary);
          break;
        case 'needsYou':
          needs.push(summary);
          break;
        case 'inExam':
          inExam.push(summary);
          break;
        case 'waitingExaminer':
          waiting.push(summary);
          break;
        case 'movingRoom':
          moving.push(summary);
          break;
        case 'preflightActive':
          preflight.push(summary);
          break;
        default:
          registered.push(summary);
      }
    });
    needs.sort(compareNeedsYou);
    return {
      needsYouRuns: needs,
      inExamRuns: inExam,
      waitingExaminerRuns: waiting,
      movingRoomRuns: moving,
      preflightActiveRuns: preflight,
      registeredRuns: registered,
      submittedRuns: submitted,
      voidRuns: voided,
      notCompletedRuns: notCompleted,
    };
  }, [rosterRuns]);

  const onTrackRuns = useMemo(
    () => [...inExamRuns, ...waitingExaminerRuns, ...movingRoomRuns],
    [inExamRuns, waitingExaminerRuns, movingRoomRuns],
  );

  const hasLiveTiles = needsYouRuns.length > 0 || onTrackRuns.length > 0;
  const liveRuns = useMemo(
    () => [...needsYouRuns, ...onTrackRuns],
    [needsYouRuns, onTrackRuns],
  );
  const observedSummary = useMemo(
    () => liveRuns.find(
      (summary) => summary.supervisorStatus === OralExamSupervisorStatus.here_with_you
        || summary.bookingId === quietObserveBookingId,
    ) ?? null,
    [liveRuns, quietObserveBookingId],
  );
  const peerLiveRuns = useMemo(() => {
    if (!observedSummary) return [];
    return liveRuns.filter((run) => run.bookingId !== observedSummary.bookingId);
  }, [liveRuns, observedSummary]);
  const nextObserveBookingId = useMemo(() => {
    if (!observedSummary || liveRuns.length < 2) return null;
    const index = liveRuns.findIndex((run) => run.bookingId === observedSummary.bookingId);
    if (index < 0) return null;
    return liveRuns[(index + 1) % liveRuns.length].bookingId;
  }, [liveRuns, observedSummary]);

  const renderLiveTile = (
    summary: OralExamBookingRunSummary,
    variant: 'needs-you' | 'on-track',
    layout: 'grid' | 'zoom' | 'compact' = 'grid',
  ) => {
    const recoveryHandlers = resolveTileRecoveryHandlers(summary);
    const colleagueVisits = viewerLecturerId
      ? colleagueVisitsOnBooking(cohort?.supervisors, summary.bookingId, viewerLecturerId)
      : [];
    const visitHolder = viewerLecturerId
      ? visitHolderAboveObserving(cohort?.supervisors, summary.bookingId, viewerLecturerId)
      : null;
    return (
      <StudentLiveTile
        variant={variant}
        layout={layout}
        summary={summary}
        onDropIn={handleEngageDropIn}
        onLeave={onLeave}
        onNext={onNext}
        onRemove={recoveryHandlers.onRemove}
        onReset={recoveryHandlers.onReset}
        onCancel={recoveryHandlers.onCancel}
        isObserving={
          summary.supervisorStatus === OralExamSupervisorStatus.here_with_you
          || summary.bookingId === quietObserveBookingId
        }
        nextObserveBookingId={layout === 'zoom' ? nextObserveBookingId : null}
        onObserveToggle={handleObserveToggle}
        rungControlsDisabled={rungControlsDisabled}
        observeError={observeErrorByBooking[summary.bookingId] ?? null}
        returnToOverviewError={
          summary.bookingId === observedSummary?.bookingId ? returnToOverviewError : null
        }
        colleagueVisits={colleagueVisits}
        visitHolder={visitHolder}
      />
    );
  };

  const showRecordingInfo = rosterRuns.some(
    (summary) => summary.runState.recordingRequired === false,
  );
  const showRecordingInLiveBar = rosterRuns.some(
    (summary) => summary.isAdmitted && summary.runState.recordingStatus === 'pending',
  );
  const colleagueNames = viewerLecturerId
    ? otherConnectedSupervisorNames(cohort?.supervisors, viewerLecturerId)
    : [];

  return (
    <VStack flex={1} minHeight={0} overflow="hidden" space="md" p="$3" testID="oral-exam-cohort-console">
      <HStack space="sm" alignItems="center" testID="cohort-live-bar">
        <Box w={10} h={10} borderRadius="$full" bg="$statusError" />
        <Text size="sm" fontWeight="bold" color="$textHeading">
          {t('oralExam.lecturer.cohort.liveBarBase', {
            defaultValue: "You're live · supervising {{count}}",
            count: rosterRuns.length,
          })}
          {showRecordingInLiveBar
            ? t('oralExam.lecturer.cohort.liveBarRecordingSuffix', {
              defaultValue: ' · recording',
            })
            : null}
          {colleagueNames.length > 0
            ? t('oralExam.lecturer.cohort.liveBarWithColleagues', {
              defaultValue: ' · with {{names}}',
              names: colleagueNames.join(', '),
            })
            : null}
        </Text>
      </HStack>

      {cohort?.supervisionAbsence?.deadlineAt ? (
        <SupervisionAbsenceCountdown deadlineAt={cohort.supervisionAbsence.deadlineAt} />
      ) : null}

      {showRecordingInfo ? (
        <OperationalFaultStudentBanner
          copyKey={RECORDING_NOT_REQUIRED_INFO.copyKey}
          defaultValue={RECORDING_NOT_REQUIRED_INFO.defaultValue}
          tone="info"
          testID="lecturer-cohort-recording-not-required-banner"
        />
      ) : null}

      <VStack
        flex={1}
        space="md"
        minHeight={0}
        style={observedSummary ? undefined : wallScrollStyle()}
        testID="cohort-console-body"
      >
        {hasLiveTiles ? (
          <VStack
            space="sm"
            flex={observedSummary ? 1 : undefined}
            minHeight={0}
            overflow={observedSummary ? 'hidden' : undefined}
            testID="cohort-live-tiles"
          >
            {!observedSummary && needsYouRuns.length > 0 ? (
              <Text
                size="xs"
                color="$textHint"
                fontWeight="bold"
                textTransform="uppercase"
                testID="cohort-band-needs-you"
              >
                {t('oralExam.lecturer.cohort.needsYou', { defaultValue: 'Needs you' })}
              </Text>
            ) : null}
            {!observedSummary && onTrackRuns.length > 0 && inExamRuns.length > 0 ? (
              <Text
                size="xs"
                color="$textHint"
                fontWeight="bold"
                textTransform="uppercase"
                testID="cohort-band-on-track"
              >
                {t('oralExam.lecturer.cohort.onTrack', { defaultValue: 'Students in exam' })}
              </Text>
            ) : null}
            {!observedSummary && waitingExaminerRuns.length > 0 ? (
              <Text
                size="xs"
                color="$textHint"
                fontWeight="bold"
                textTransform="uppercase"
                testID="cohort-band-waiting-examiner"
              >
                {t('oralExam.lecturer.cohort.waitingExaminerHeader', {
                  defaultValue: 'In exam room',
                })}
              </Text>
            ) : null}
            {!observedSummary && movingRoomRuns.length > 0 ? (
              <Text
                size="xs"
                color="$textHint"
                fontWeight="bold"
                textTransform="uppercase"
                testID="cohort-band-moving-room"
              >
                {t('oralExam.lecturer.cohort.movingRoomHeader', {
                  defaultValue: 'Moving to exam room',
                })}
              </Text>
            ) : null}
            {observedSummary ? (
              <Box
                flex={1}
                minHeight={0}
                w="$full"
                flexWrap="nowrap"
                style={observeStageColumnStyle()}
                testID="oral-exam-observe-stage"
              >
                <Box
                  key={observedSummary.bookingId}
                  testID={`oral-exam-observe-stage-${observedSummary.bookingId}`}
                  style={observeHeroCellStyle()}
                >
                  {renderLiveTile(
                    observedSummary,
                    needsYou(observedSummary) ? 'needs-you' : 'on-track',
                    'zoom',
                  )}
                </Box>
                {peerLiveRuns.length > 0 ? (
                  <Box
                    testID="oral-exam-observe-strip"
                    style={observeStripRowStyle()}
                  >
                    {peerLiveRuns.map((summary) => (
                      <Box
                        key={summary.bookingId}
                        style={observeStripCellStyle()}
                      >
                        {renderLiveTile(
                          summary,
                          needsYou(summary) ? 'needs-you' : 'on-track',
                          'compact',
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Box>
            ) : (
              <Box
                style={wallOnTrackGridStyle()}
                testID="cohort-on-track-tiles"
              >
                {liveRuns.map((summary) => {
                  const variant = needsYou(summary) ? 'needs-you' : 'on-track';
                  return (
                    <Box
                      key={summary.bookingId}
                      style={wallTileCellStyle()}
                    >
                      {renderLiveTile(summary, variant, 'grid')}
                    </Box>
                  );
                })}
              </Box>
            )}
          </VStack>
        ) : null}

        {preflightActiveRuns.length > 0 && (
          <VStack space="sm" testID="cohort-band-preflight-active">
            <Text size="xs" color="$textHint" fontWeight="bold" textTransform="uppercase">
              {t('oralExam.lecturer.cohort.preflightActiveHeader', {
                defaultValue: 'Pre-flight / ID review',
              })}
            </Text>
            <HStack space="md" flexWrap="wrap" alignItems="stretch">
              {preflightActiveRuns.map((summary) => (
                <Box
                  key={summary.bookingId}
                  flexGrow={1}
                  flexShrink={1}
                  flexBasis={0}
                  minWidth={320}
                  maxWidth={520}
                >
                  <PreflightJourneyTile summary={summary} />
                </Box>
              ))}
            </HStack>
          </VStack>
        )}

        {registeredRuns.length > 0 && (
          <VStack space="sm" testID="cohort-band-registered">
            <Text size="xs" color="$textHint" fontWeight="bold" textTransform="uppercase">
              {t('oralExam.lecturer.cohort.registeredHeader', { defaultValue: 'Registered — waiting to join' })}
            </Text>
            <HStack space="md" flexWrap="wrap" alignItems="stretch">
              {registeredRuns.map((summary) => (
                <Box
                  key={summary.bookingId}
                  flexGrow={1}
                  flexShrink={1}
                  flexBasis={0}
                  minWidth={320}
                  maxWidth={520}
                >
                  <RegisteredStudentTile summary={summary} />
                </Box>
              ))}
            </HStack>
          </VStack>
        )}

        {voidRuns.length > 0 && (
          <VStack space="sm" testID="cohort-band-void">
            <Text size="xs" color="$textHint" fontWeight="bold" textTransform="uppercase">
              {t('oralExam.lecturer.cohort.voidHeader', {
                defaultValue: 'Session void — rebook required',
              })}
            </Text>
            <HStack space="md" flexWrap="wrap" alignItems="stretch">
              {voidRuns.map((summary) => (
                <Box
                  key={summary.bookingId}
                  flexGrow={1}
                  flexShrink={1}
                  flexBasis={0}
                  minWidth={320}
                  maxWidth={520}
                >
                  <VoidStudentTile summary={summary} />
                </Box>
              ))}
            </HStack>
          </VStack>
        )}

        {notCompletedRuns.length > 0 && (
          <VStack space="sm" testID="cohort-band-not-completed">
            <Text size="xs" color="$textHint" fontWeight="bold" textTransform="uppercase">
              {t('oralExam.lecturer.cohort.notCompleted', { defaultValue: 'Not completed' })}
            </Text>
            <HStack space="md" flexWrap="wrap" alignItems="stretch">
              {notCompletedRuns.map((summary) => (
                <Box
                  key={summary.bookingId}
                  flexGrow={1}
                  flexShrink={1}
                  flexBasis={0}
                  minWidth={320}
                  maxWidth={520}
                >
                  <NotCompletedStudentTile summary={summary} />
                </Box>
              ))}
            </HStack>
          </VStack>
        )}

        {!observedSummary && submittedRuns.length > 0 && (
          <VStack space="sm" testID="cohort-band-submitted">
            <Text size="xs" color="$textHint" fontWeight="bold" textTransform="uppercase">
              {t('oralExam.lecturer.cohort.submitted', { defaultValue: 'Submitted' })}
            </Text>
            <VStack space="xs" style={wallWidthStyle()} testID="cohort-submitted-list">
              {submittedRuns.map((summary) => (
                <StudentLiveTile
                  key={summary.bookingId}
                  variant="submitted"
                  summary={summary}
                  onDropIn={onDropIn}
                  onNext={onNext}
                />
              ))}
            </VStack>
          </VStack>
        )}
      </VStack>

      <HStack
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        space="sm"
        pt="$2"
        pb="$4"
        borderTopWidth={1}
        borderTopColor="$borderLight"
        testID="cohort-action-bar"
      >
        {closeError ? (
          <Text size="sm" color="$statusError" testID="cohort-close-error" flex={1}>
            {closeError}
          </Text>
        ) : null}
        <Button
          size="sm"
          action="primary"
          onPress={handleRequestEnd}
          isDisabled={closeCohortMutation.isPending || !canCloseCohort}
          testID="cohort-end"
        >
          {closeCohortMutation.isPending ? (
            <ButtonSpinner mr="$1" />
          ) : (
            <ButtonIcon as={Square} mr="$1" />
          )}
          <ButtonText>{t('oralExam.lecturer.cohort.endCohort', { defaultValue: 'End exam' })}</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
